/**
 * Servicio de registro / acceso con código OTP del portal del asegurado.
 * Consume los endpoints backend bajo /api/v1/insurance/insured/auth/
 * (citamed247_backend: insurance/views/insured_auth.py).
 *
 * La sesión del asegurado se guarda en claves propias de localStorage
 * (`insured_token`, `insured_user`) para no mezclarse con la sesión del
 * personal del dashboard (`token`).
 */
import { INSURED_REGISTRATION_ENABLED } from './config';
import { BASE_URL } from '@/constants';

const TOKEN_KEY = 'insured_token';
const USER_KEY = 'insured_user';

/**
 * Error de la API. Además del mensaje expone `status` y el cuerpo (`data`),
 * p.ej. `data.retry_after_seconds` cuando se pide un código antes de tiempo.
 */
async function buildError(res) {
  let data = null;
  let message = `Error ${res.status}`;
  try {
    data = await res.json();
    if (data.error || data.detail) {
      message = data.error || data.detail;
    } else if (data && typeof data === 'object') {
      // Errores de validación de DRF: { campo: ["mensaje", ...] }
      const first = Object.values(data).flat()[0];
      if (first) message = String(first);
    }
  } catch {
    // ignore
  }
  const error = new Error(message);
  error.status = res.status;
  error.data = data;
  return error;
}

async function postJson(path, body) {
  const res = await fetch(`${BASE_URL}insurance/insured/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await buildError(res);
  return res.json();
}

/**
 * Estado de una cédula.
 * @returns {Promise<{exists: boolean, registered: boolean}>}
 */
export function checkInsuredCi(ci) {
  return postJson('check-ci/', { ci });
}

/**
 * Paso 1 del registro: valida correo + teléfono y envía un código OTP al
 * correo. Todavía no crea el usuario. También sirve para "Reenviar código".
 * Si se pide antes de tiempo, el error trae `data.retry_after_seconds`.
 *
 * Datos del titular (Bypass RMS, Oct 2026): cuando el `check-ci` entró en
 * bypass (la cédula no estaba en la BD), el frontend debe enviar
 * `name` (obligatorio), `sex` y `birthdate` (opcionales) para que el
 * backend cree un Person placeholder después del OTP. Si la cédula ya
 * estaba registrada, `name`/`sex`/`birthdate` se ignoran.
 *
 * `phoneNumber` es opcional: si el titular ya tiene teléfono guardado, el
 * frontend lo salta y el backend completa User.phone_number con Person.phone.
 *
 * @returns {Promise<{masked_email: string, expires_in_seconds: number, resend_after_seconds: number}>}
 */
export function startInsuredRegistration({
  ci,
  email,
  phoneNumber,
  name,
  sex,
  birthdate,
}) {
  const payload = { ci, email };
  if (phoneNumber) payload.phone_number = phoneNumber;
  if (name) payload.name = name;
  if (sex) payload.sex = sex;
  if (birthdate) payload.birthdate = birthdate;
  return postJson('register/', payload);
}

/**
 * Paso 2 del registro: valida el código, crea el usuario con la cédula
 * asociada y guarda la sesión.
 * @returns {Promise<{token: string, user: object}>}
 */
export async function verifyInsuredRegistration({ ci, code }) {
  const data = await postJson('register/verify/', { ci, code });
  saveInsuredSession(data);
  return data;
}

/**
 * Envía (o reenvía) el código OTP al correo registrado de la cédula.
 * Si se pide antes de tiempo, el error trae `data.retry_after_seconds`.
 * @returns {Promise<{masked_email: string, expires_in_seconds: number, resend_after_seconds: number}>}
 */
export function requestInsuredOtp(ci) {
  return postJson('otp/request/', { ci });
}

/**
 * Valida el código OTP. Guarda la sesión.
 * @returns {Promise<{token: string, user: object}>}
 */
export async function verifyInsuredOtp({ ci, code }) {
  const data = await postJson('otp/verify/', { ci, code });
  saveInsuredSession(data);
  return data;
}

/** Datos del asegurado autenticado (requiere sesión). */
export async function getInsuredMe() {
  const token = getInsuredToken();
  if (!token) throw new Error('No hay sesión del asegurado.');
  const res = await fetch(`${BASE_URL}insurance/insured/auth/me/`, {
    headers: { Accept: 'application/json', Authorization: `Token ${token}` },
  });
  if (!res.ok) throw await buildError(res);
  return res.json();
}

export function saveInsuredSession({ token, user }) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // ignore
  }
}

export function getInsuredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Usuario de la sesión del asegurado ({ ci, email, persons, ... }) o null. */
export function getInsuredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
}

/**
 * Cabecera Authorization para las llamadas del portal (services/insuredService.js).
 * Con el switch apagado o sin sesión devuelve {} y las llamadas quedan igual
 * que antes.
 */
export function insuredAuthHeader() {
  if (!INSURED_REGISTRATION_ENABLED) return {};
  const token = getInsuredToken();
  return token ? { Authorization: `Token ${token}` } : {};
}

/**
 * Si el backend responde 401 (sesión inválida) con el switch encendido,
 * borra la sesión y vuelve al login del portal.
 */
export function handleInsuredUnauthorized(status) {
  if (!INSURED_REGISTRATION_ENABLED || status !== 401) return;
  clearInsuredSession();
  window.location.assign('/asegurado/login');
}

export function clearInsuredSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
}
